import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorDetail, fetchAssignments, humanError, setAssignmentActive } from '../../api';
import { ConfirmPanel } from '../../components/ConfirmPanel';
import { ExcludedAttemptHistory } from '../../components/ExcludedAttemptHistory';
import { Icon } from '../../components/icons';
import type { IconName } from '../../components/icons';
import { ASSIGNMENT_STATE_LABEL, AssignmentRow, givenIntro, OverviewDetail } from '../../model';
import type { WorkspaceNotice } from '../../notices';

export interface GivenTabProps {
  detail: OverviewDetail;
  reload: () => Promise<void>;
  notify: (notice: WorkspaceNotice) => void;
  onAssign: () => void;
}

const initials = (name: string): string => name.split(' ').filter(Boolean).map((part) => part.charAt(0)).join('').slice(0, 2);

/** Результат последней попытки значком, как в макете: «Сдал — 90%», «Не сдал — 45%», «Ещё не проходил». */
function resultPill(row: AssignmentRow): { className: string; icon: IconName; label: string } {
  if (row.latest_attempt_score != null && row.latest_attempt_is_passed != null) {
    const percent = Math.round(row.latest_attempt_score);
    return row.latest_attempt_is_passed
      ? { className: 'tw-pill-pass', icon: 'check', label: `Сдал — ${percent}%` }
      : { className: 'tw-pill-fail', icon: 'x', label: `Не сдал — ${percent}%` };
  }
  if (row.excluded_attempts_count) {
    return { className: 'tw-pill-new', icon: 'clock', label: row.effective_state === 'available' ? 'Новая попытка доступна' : 'Техническая попытка не учитывается' };
  }
  return { className: 'tw-pill-new', icon: 'clock', label: 'Ещё не проходил' };
}

/** Открытие доступа, на которое сервер ответил «похожий тест»: ждёт согласия администратора. */
interface SimilarAsk {
  row: AssignmentRow;
  done: string;
  titles: string;
}

export const GivenTab: React.FC<GivenTabProps> = ({ detail, reload, notify, onAssign }) => {
  const [rows, setRows] = useState<AssignmentRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [similar, setSimilar] = useState<SimilarAsk | null>(null);
  const requestRef = useRef(0);
  const archived = detail.status === 'archived';

  const load = useCallback(async () => {
    const request = ++requestRef.current; // устаревший ответ не должен затереть свежий
    try {
      const data = await fetchAssignments(detail.id);
      if (request !== requestRef.current) return;
      setRows(data);
      setFailed(false);
    } catch {
      if (request === requestRef.current) setFailed(true);
    }
  }, [detail.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Тест ушёл в архив: вопрос про похожий тест снимается и не возвращается после «Вернуть из архива».
  useEffect(() => {
    if (archived) setSimilar(null);
  }, [archived]);

  const change = async (row: AssignmentRow, active: boolean, done: string, allowSimilar = false) => {
    setBusyId(row.id);
    try {
      await setAssignmentActive(row.id, active, allowSimilar);
      notify({ kind: 'ok', text: done });
      await load();
      await reload();
    } catch (error) {
      const info = errorDetail(error);
      if (info?.code === 'similar_test_confirmation_required') {
        const list = Array.isArray(info.conflicts) ? (info.conflicts as Array<{ test_title?: string }>) : [];
        setSimilar({ row, done, titles: list.map((item) => `«${item.test_title ?? 'другой тест'}»`).join(', ') });
        return;
      }
      notify({ kind: 'info', text: humanError(error, 'Не получилось изменить доступ. Попробуйте ещё раз.') });
    } finally {
      setBusyId(null);
    }
  };

  const sorted = (rows ?? []).slice().sort((left, right) => (left.user_name ?? '').localeCompare(right.user_name ?? '', 'ru'));
  // Архив сервер не проверяет (`PATCH /test-assignments/{id}`): кнопки доступа скрыты здесь, иначе у архивного теста появится выдача «Выдано».
  const canChange = !archived && sorted.some((row) => ['available', 'paused', 'prepared'].includes(row.effective_state));

  return (
    <div className="tw-card tw-stack-sm">
      <h2>Кому выдан тест</h2>
      <p className="tw-help">{givenIntro(detail)}</p>
      {failed && <p className="tw-help">Не получилось загрузить выдачи. Обновите страницу и попробуйте ещё раз.</p>}
      {sorted.length > 0 && (
        <ul className="tw-qlist">
          {sorted.map((row) => {
            const name = row.user_name ?? 'Сотрудник';
            const result = resultPill(row);
            return (
              <React.Fragment key={row.id}>
                <li className="tw-qrow">
                  <span className="tw-qnum">{initials(name)}</span>
                  <span className="tw-qbody">
                    <span className="tw-qtext">{name}</span>
                    <span className="tw-qmeta">{ASSIGNMENT_STATE_LABEL[row.effective_state]}</span>
                  </span>
                  <span className={`tw-pill ${result.className}`}>
                    <Icon name={result.icon} size={20} />
                    <span>{result.label}</span>
                  </span>
                  {!archived && row.effective_state === 'available' && (
                    <button
                      type="button"
                      className="tw-btn tw-btn-small"
                      aria-label={`Приостановить: ${name}`}
                      disabled={busyId === row.id}
                      onClick={() => void change(row, false, 'Доступ приостановлен.')}
                    >
                      Приостановить
                    </button>
                  )}
                  {!archived && row.effective_state === 'paused' && (
                    <button
                      type="button"
                      className="tw-btn tw-btn-small"
                      aria-label={`Возобновить: ${name}`}
                      disabled={busyId === row.id}
                      onClick={() => void change(row, true, 'Доступ возобновлён.')}
                    >
                      Возобновить
                    </button>
                  )}
                  {!archived && row.effective_state === 'prepared' && (
                    <button
                      type="button"
                      className="tw-btn tw-btn-small"
                      aria-label={`Открыть доступ: ${name}`}
                      disabled={busyId === row.id}
                      onClick={() => void change(row, true, 'Доступ открыт.')}
                    >
                      Открыть доступ
                    </button>
                  )}
                </li>
                {/* Вопрос про похожий тест стоит сразу под строкой, чья кнопка его вызвала: в длинном списке он виден там, где нажали.
                    В архиве его нет: сервер архив не проверяет, и «Всё равно открыть» открыло бы доступ к архивному тесту. */}
                {similar && similar.row.id === row.id && !archived && (
                  <li key={`${row.id}-similar`}>
                    <ConfirmPanel
                      title="Похожий тест уже выдан"
                      text={`У сотрудника «${name}» уже есть тест с почти такими же вопросами: ${similar.titles}. Открыть доступ и к этому?`}
                      confirmLabel="Всё равно открыть"
                      cancelLabel="Не открывать"
                      onConfirm={() => {
                        const ask = similar;
                        setSimilar(null);
                        void change(ask.row, true, ask.done, true);
                      }}
                      onCancel={() => setSimilar(null)}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      )}
      {canChange && (
        <p className="tw-help">
          «Приостановить» — сотрудник перестанет видеть тест. «Возобновить» и «Открыть доступ» — сотрудник увидит тест и сможет его пройти.
        </p>
      )}
      <ExcludedAttemptHistory key={detail.id} testId={detail.id} />
      <p className="tw-help">
        Продление, пересдача и история — на{' '}
        <Link to={`/admin/board?test=${detail.id}`}>доске назначений</Link>.
      </p>
      {!archived && (
        <div className="tw-row tw-btn-row">
          <button type="button" className="tw-btn tw-btn-primary" onClick={onAssign}>Дать тест сотрудникам</button>
        </div>
      )}
    </div>
  );
};
